import { AriaAttributes, useState } from 'react';

import { ChevronDownIcon } from '../../../assets/icons/index';
import { StringKey } from '../../../lang/index';
import { Order } from '../../../types/general.types';
import { AppFormattedMessage } from '../../app-formatted-message/index';
import Button from '../../button/index';
import { TableHeaderRowData } from '../table';

type SortState<TColumn extends string> = {
  field: null | TColumn;
  order: null | Order;
};

const UNSORTED = { field: null, order: null };

// `initialSort` lets a table that remounts pick up the sort its owner still holds.
export const useTableSort = <TColumn extends string>(
  columnsToSort: TColumn[],
  columnTranslationKey: Record<TColumn, StringKey>,
  callback?: (state: SortState<TColumn>) => void,
  initialSort: SortState<TColumn> = UNSORTED,
) => {
  const [sort, setSort] = useState<SortState<TColumn>>(initialSort);

  // The callback runs in the click, not in a state updater: React may run an updater while
  // rendering, and a callback that sets its owner's state there would update another component.
  // Another column starts ascending; the same one goes ascending, descending, then unsorted.
  const handleSort = (field: TColumn) => {
    let newState: SortState<TColumn> = { field, order: Order.ASC };

    if (sort.field === field && sort.order === Order.ASC) {
      newState = { field, order: Order.DESC };
    }
    if (sort.field === field && sort.order === Order.DESC) {
      newState = { field: null, order: null };
    }

    setSort(newState);
    callback?.(newState);
  };

  const columnsTitle: TableHeaderRowData<TColumn> = Object.fromEntries(
    Object.entries(columnTranslationKey).map(([key, value]) => {
      if (!columnsToSort.includes(key)) return [key, value];

      const isCurrentColumnSorted = sort.field === key;

      return [
        key,
        <div className="flex w-fit items-center gap-1" onClick={() => handleSort(key)}>
          <span className="text-label-md font-[450] text-[#172335]">
            <AppFormattedMessage id={value} />
          </span>
          <Button
            className="relative flex size-4 justify-center rounded hover:bg-gray-200"
            styleType="NONE"
          >
            {isCurrentColumnSorted && sort.order === Order.DESC ? (
              <ChevronDownIcon className="w-2 rotate-180" />
            ) : isCurrentColumnSorted && sort.order === Order.ASC ? (
              <ChevronDownIcon className="w-2" />
            ) : (
              <>
                <ChevronDownIcon className="absolute -top-[3px] w-2 rotate-180" />
                <ChevronDownIcon className="absolute -bottom-[3px] w-2" />
              </>
            )}
          </Button>
        </div>,
      ];
    }),
  );

  // What each sortable column announces to a screen reader: its direction, or none.
  const ariaSort = Object.fromEntries(
    columnsToSort.map((column): [TColumn, AriaAttributes['aria-sort']] => [
      column,
      sort.field === column ? (sort.order === Order.DESC ? 'descending' : 'ascending') : 'none',
    ]),
  ) as PartialRecord<TColumn, AriaAttributes['aria-sort']>;

  return [columnsTitle, sort, ariaSort] as const;
};
